import React, { useContext, useState, useRef, useEffect } from 'react'
import './Navbar.css'
import logo from '../Assets/logo.png'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ShopContext } from '../../Context/ShopContext'
import { 
  RiLoaderLine
} from "react-icons/ri";
import { 
  FaHome,
  FaUser,
  FaShoppingCart,
  FaTshirt,
  FaShoePrints,
  FaFemale,
  FaMale,
  FaChild,
  FaFire,
  FaStar,
  FaSearch,
  FaTimes,
  FaSignOutAlt,
  FaBars,
  FaArrowRight,
  FaUserCircle,
  FaShoppingBag,
  FaHeart,
  FaCog
} from "react-icons/fa";

const Navbar = () => {
  const { 
    getTotalCartItems, 
    allProducts = [], 
    searchProducts,
    performClientSideSearch,
    showMessage,
  } = useContext(ShopContext);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const searchRef = useRef();
  const searchInputRef = useRef();
  const mobileMenuRef = useRef();
  const profileDropdownRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  // Close search results and profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close search results
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      
      // Close search bar
      if (!event.target.closest('.search-icon-container') && 
          !event.target.closest('.search-bar-wrapper') &&
          showSearch) {
        setShowSearch(false);
        setShowResults(false);
        setSearchQuery('');
      }
      
      // Close mobile menu
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('.mobile-menu-toggle')) {
        setShowMobileMenu(false);
      }
      
      // Close profile dropdown
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target) && 
          !event.target.closest('.profile-icon-container')) {
        setShowProfileDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch, showProfileDropdown]);

  // Focus search input when search bar is shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Handle body scroll when search is open
  useEffect(() => {
    if (showSearch) {
      document.body.classList.add('search-open');
    } else {
      document.body.classList.remove('search-open');
    }
    
    return () => {
      document.body.classList.remove('search-open');
    };
  }, [showSearch]);

  // Check URL hash on location change
  useEffect(() => {
    if (location.hash === '#new-arrivals-section' && location.pathname === '/') {
      setTimeout(() => {
        scrollToNewArrivalsSection();
      }, 500);
    }
  }, [location]);

  // Check authentication status on component mount and location change
  useEffect(() => {
    // This will run when the component mounts and when location changes
    const token = localStorage.getItem('auth-token');
    console.log('Auth token in Navbar:', token ? 'Present' : 'Not present');
  }, [location]);

  // Debounce function for API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // Handle search with API call and client-side fallback
  const handleSearchApiCall = async (query) => {
    if (query.trim().length === 0) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    if (query.trim().length >= 1) {
      setIsSearching(true);
      
      try {
        const results = await searchProducts(query);
        
        if (results && results.length > 0) {
          setSearchResults(results);
          setShowResults(true);
        } else {
          const clientResults = performClientSideSearch(query);
          setSearchResults(clientResults);
          setShowResults(clientResults.length > 0);
        }
        
      } catch (error) {
        console.error('Search error:', error);
        const clientResults = performClientSideSearch(query);
        setSearchResults(clientResults);
        setShowResults(clientResults.length > 0);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const debouncedSearch = debounce(handleSearchApiCall, 300);

  // Handle search input changes
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim().length >= 1) {
      const immediateResults = performClientSideSearch(query);
      if (immediateResults.length > 0) {
        setSearchResults(immediateResults);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
    
    debouncedSearch(query);
  };

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  // Handle product click from search results
  const handleProductClick = (product) => {
    const productId = product.id || product._id;
    if (productId) {
      navigate(`/product/${productId}`);
    } else {
      console.error("Product ID not found:", product);
      showMessage('Cannot navigate to product details', 'error');
    }
    setShowResults(false);
    setShowSearch(false);
    setSearchQuery('');
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Toggle search bar
  const toggleSearch = () => {
    if (!showSearch) {
      setShowSearch(true);
      setShowMobileMenu(false);
      setShowProfileDropdown(false);
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      setShowSearch(false);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    setShowSearch(false);
    setShowProfileDropdown(false);
  };

  // Toggle profile dropdown
  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    setShowSearch(false);
    setShowMobileMenu(false);
  };

  // View all search results
  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const totalItems = getTotalCartItems();
  
  const getBadgeSizeClass = () => {
    if (totalItems > 99) return 'triple-digit';
    if (totalItems > 9) return 'double-digit';
    return '';
  }

  // Get product image URL
  const getProductImage = (product) => {
    if (product.main_image) return product.main_image;
    if (product.image) return product.image;
    if (product.images && product.images.length > 0) return product.images[0];
    return 'https://via.placeholder.com/50x50?text=Product';
  };

  // Get product price
  const getProductPrice = (product) => {
    if (product.new_price) return product.new_price;
    if (product.price) return product.price;
    return 0;
  };

  // Format price
  const formatPrice = (price) => {
    return `Rs ${price.toLocaleString()}`;
  };

  // Handle category navigation
  const handleCategoryNavigation = (category) => {
    navigate(`/${category}`);
    setShowMobileMenu(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-name');
    localStorage.removeItem('user-email');
    setShowProfileDropdown(false);
    setShowMobileMenu(false);
    navigate('/');
    window.location.reload(); // Refresh to update state
  };

  // Handle profile navigation
  const handleProfileNavigation = () => {
    setShowProfileDropdown(false);
    setShowMobileMenu(false);
    navigate('/profile');
  };

  // Handle quick link click
  const handleClothingClick = () => {
    const random = Math.random();
    if (random < 0.5) {
      navigate('/men');
    } else {
      navigate('/women');
    }
    setShowMobileMenu(false);
  };

  // Handle footwear click
  const handleFootwearClick = () => {
    navigate('/shoes');
    setShowMobileMenu(false);
  };

  // Handle Home Click
  const handleHomeClick = (e) => {
    setShowMobileMenu(false);
    setShowSearch(false);
    setShowResults(false);
    setSearchQuery('');
    setShowProfileDropdown(false);
    
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      if (window.location.hash) {
        window.history.replaceState(null, null, ' ');
      }
    }
  };

  // Handle new arrivals click
  const handleNewArrivalsClick = (e) => {
    setShowMobileMenu(false);
    setShowSearch(false);
    setShowProfileDropdown(false);
    
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToNewArrivalsSection();
    } else {
      e.preventDefault();
      navigate('/#new-arrivals-section');
    }
  };

  // Function to scroll to the new arrivals section
  const scrollToNewArrivalsSection = () => {
    const newArrivalsSection = document.getElementById('new-arrivals-section');
    
    if (newArrivalsSection) {
      const navbar = document.querySelector('.navbar');
      const navbarHeight = navbar ? navbar.offsetHeight : 70;
      
      const horizontalScrollbar = document.querySelector('.horizontal-scrollbar-container');
      const scrollbarHeight = horizontalScrollbar && window.innerWidth <= 767 ? 
                             horizontalScrollbar.offsetHeight : 0;
      
      const totalOffset = navbarHeight + scrollbarHeight + 20;
      
      const elementPosition = newArrivalsSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - totalOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      newArrivalsSection.classList.add('highlighted');
      
      setTimeout(() => {
        newArrivalsSection.classList.remove('highlighted');
      }, 2000);
      
      window.history.replaceState(null, null, '#new-arrivals-section');
      
      setTimeout(() => {
        if (window.location.hash === '#new-arrivals-section') {
          window.history.replaceState(null, null, ' ');
        }
      }, 1000);
    } else {
      navigate('/#new-arrivals-section');
    }
  };

  // Handle mobile home click
  const handleMobileHomeClick = () => {
    setShowMobileMenu(false);
    
    if (window.location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      if (window.location.hash) {
        window.history.replaceState(null, null, ' ');
      }
    } else {
      navigate('/');
    }
  };

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('auth-token');

  return (
    <>
      <div className='navbar'>
        {/* Mobile Menu Toggle */}
        <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <FaBars />
        </div>

        <div className="nav-logo">
          <Link to="/" onClick={handleHomeClick}>
            <img src={logo} alt="logo" />
          </Link>
          <p>
            <span>NEPLI<span>SHOP</span></span>  
          </p>
        </div>

        {/* Desktop Navigation Menu */}
        <ul className="nav-menu">
          <li>
            <Link to="/" onClick={handleHomeClick}>
              <FaHome className="nav-icon" />
              Home
            </Link>
          </li>
          <li>
            <Link to="/women">
              <FaFemale className="nav-icon" />
              Women
            </Link>
          </li>
          <li>
            <Link to="/men">
              <FaMale className="nav-icon" />
              Men
            </Link>
          </li>
          <li>
            <Link to="/kids">
              <FaChild className="nav-icon" />
              Kids
            </Link>
          </li>
          <li>
            <a 
              href="#new-arrivals-section" 
              onClick={handleNewArrivalsClick}
              className="nav-new-arrivals-link"
            >
              <FaFire className="nav-icon" />
              New Arrivals
            </a>
          </li>
        </ul>

        <div className="nav-icons">
          {/* Search Icon */}
          <div className="search-icon-container" onClick={toggleSearch}>
            <FaSearch className="nav-icon-rounded" />
          </div>

          {/* Profile Icon with Dropdown */}
          <div className="profile-icon-container" ref={profileDropdownRef}>
            {isLoggedIn ? (
              <>
                <button 
                  className="profile-icon-btn"
                  onClick={toggleProfileDropdown}
                  aria-label="Profile menu"
                >
                  <FaUserCircle className="nav-icon-rounded profile-icon" />
                </button>
                
                {showProfileDropdown && (
                  <div className="profile-dropdown-menu">
                    <div className="dropdown-header">
                      <FaUserCircle className="dropdown-header-icon" />
                      <div className="dropdown-user-info">
                        <h4>{localStorage.getItem('user-name') || 'NepliShop User'}</h4>
                        <p>{localStorage.getItem('user-email') || 'user@example.com'}</p>
                      </div>
                    </div>
                    
                    <div className="dropdown-items">
                      <button 
                        className="dropdown-item"
                        onClick={handleProfileNavigation}
                      >
                        <FaUser />
                        <span>My Profile</span>
                      </button>
                      
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          setShowProfileDropdown(false);
                          navigate('/profile?tab=orders');
                        }}
                      >
                        <FaShoppingBag />
                        <span>Order History</span>
                      </button>
                      
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          setShowProfileDropdown(false);
                          navigate('/profile?tab=wishlist');
                        }}
                      >
                        <FaHeart />
                        <span>Wishlist</span>
                      </button>
                      
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          setShowProfileDropdown(false);
                          navigate('/profile?tab=settings');
                        }}
                      >
                        <FaCog />
                        <span>Settings</span>
                      </button>
                      
                      <div className="dropdown-divider"></div>
                      
                      <button 
                        className="dropdown-item logout"
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Link 
                to='/login' 
                className="profile-icon-btn"
                onClick={() => setShowProfileDropdown(false)}
              >
                <FaUser className="nav-icon-rounded profile-icon" />
              </Link>
            )}
          </div>

          {/* Cart Icon */}
          <Link to='/cart' className="cart-icon-container">
            <FaShoppingCart className="nav-icon-rounded" />
            {totalItems > 0 && (
              <div className={`nav-cart-count ${getBadgeSizeClass()}`}>
                {totalItems > 99 ? '99+' : totalItems}
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Search Bar Overlay */}
      {showSearch && (
        <div className="search-bar-overlay">
          <div className="search-bar-wrapper" ref={searchRef}>
            <form className="search-bar-form" onSubmit={handleSearchSubmit}>
              <div className="search-bar-input-wrapper">
                <FaSearch className="search-bar-icon" />
                <input
                  type="text"
                  ref={searchInputRef}
                  className="search-bar-input"
                  placeholder="Search for products, brands, and more..."
                  value={searchQuery}
                  onChange={handleSearch}
                  autoComplete="off"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="search-bar-clear-btn"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
                <button
                  type="button"
                  className="search-bar-close"
                  onClick={toggleSearch}
                  aria-label="Close search"
                >
                  <FaTimes />
                </button>
              </div>
            </form>

            {/* Search Results */}
            {showResults && searchQuery.trim() && (
              <div className="search-bar-results">
                {/* Results header */}
                {searchResults.length > 0 && !isSearching && (
                  <div className="search-bar-results-header">
                    <h4>
                      <FaSearch />
                      Search Results
                    </h4>
                    <div className="search-bar-results-count">
                      {searchResults.length} {searchResults.length === 1 ? 'item' : 'items'} found
                    </div>
                  </div>
                )}

                {/* Loading state */}
                {isSearching && (
                  <div className="search-bar-loading">
                    <RiLoaderLine className="loading-spinner" />
                    <span>Searching...</span>
                  </div>
                )}

                {/* No results state */}
                {!isSearching && searchResults.length === 0 && searchQuery.trim() && (
                  <div className="search-bar-no-results">
                    <FaSearch className="search-bar-no-results-icon" />
                    <p>No results found for "{searchQuery}"</p>
                    <p className="search-suggestions">Try different keywords or check spelling</p>
                  </div>
                )}

                {/* Results list */}
                {!isSearching && searchResults.length > 0 && (
                  <>
                    <div className="search-results-list">
                      {searchResults.slice(0, 5).map((product) => (
                        <div
                          key={product.id || product._id}
                          className="search-bar-result-item"
                          onClick={() => handleProductClick(product)}
                        >
                          <img
                            src={getProductImage(product)}
                            alt={product.name}
                            className="search-bar-result-image"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/50x50?text=Product';
                            }}
                          />
                          <div className="search-bar-result-details">
                            <h5 className="search-bar-result-title">{product.name}</h5>
                            <p className="search-bar-result-price">
                              {formatPrice(getProductPrice(product))}
                            </p>
                            <p className="search-bar-result-category">
                              <FaShoppingBag />
                              {product.category || product.productType || 'Product'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* View all results */}
                    {searchResults.length > 5 && (
                      <div
                        className="search-bar-view-all"
                        onClick={handleViewAllResults}
                      >
                        View all {searchResults.length} results
                        <FaArrowRight />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu Sidebar */}
      {showMobileMenu && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu}>
          <div className="mobile-menu-sidebar" ref={mobileMenuRef} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-menu-logo">
                <img src={logo} alt="logo" />
                <span>NEPLISHOP</span>
              </div>
              <button className="mobile-menu-close" onClick={toggleMobileMenu}>
                <FaTimes />
              </button>
            </div>
            
            <div className="mobile-menu-content">
              <div className="mobile-user-section">
                {isLoggedIn ? (
                  <div className="mobile-profile-info">
                    <FaUserCircle className="mobile-profile-icon" />
                    <div className="mobile-profile-details">
                      <h4>{localStorage.getItem('user-name') || 'NepliShop User'}</h4>
                      <p>{localStorage.getItem('user-email') || 'user@example.com'}</p>
                    </div>
                    <div className="mobile-profile-actions">
                      <button 
                        onClick={() => {
                          setShowMobileMenu(false);
                          navigate('/profile');
                        }}
                        className="mobile-profile-btn"
                      >
                        My Profile
                      </button>
                      <button onClick={handleLogout} className="mobile-logout-btn">
                        <FaSignOutAlt /> Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link to='/login' onClick={toggleMobileMenu} className="mobile-login-link">
                    <FaUser className="mobile-login-icon" />
                    <span>Login / Sign Up</span>
                  </Link>
                )}
              </div>

              <div className="mobile-menu-categories">
                <h3> Main Menu</h3>
                <ul>
                  <li onClick={handleMobileHomeClick}>
                    <FaHome /> Home
                  </li>
                  <li onClick={() => handleCategoryNavigation('women')}>
                    <FaFemale /> Women's Fashion
                  </li>
                  <li onClick={() => handleCategoryNavigation('men')}>
                    <FaMale /> Men's Fashion
                  </li>
                  <li onClick={() => handleCategoryNavigation('kids')}>
                    <FaChild /> Kids' Fashion
                  </li>
                  <li onClick={handleNewArrivalsClick}>
                    <FaFire /> New Arrivals
                  </li>
                </ul>
              </div>

              {isLoggedIn && (
                <div className="mobile-menu-account">
                  <h3><FaUserCircle /> My Account</h3>
                  <ul>
                    <li onClick={() => {
                      setShowMobileMenu(false);
                      navigate('/profile');
                    }}>
                      <FaUser /> My Profile
                    </li>
                    <li onClick={() => {
                      setShowMobileMenu(false);
                      navigate('/profile?tab=orders');
                    }}>
                      <FaShoppingBag /> Order History
                    </li>
                    <li onClick={() => {
                      setShowMobileMenu(false);
                      navigate('/profile?tab=wishlist');
                    }}>
                      <FaHeart /> Wishlist
                    </li>
                    <li onClick={() => {
                      setShowMobileMenu(false);
                      navigate('/profile?tab=settings');
                    }}>
                      <FaCog /> Settings
                    </li>
                  </ul>
                </div>
              )}

              <div className="mobile-menu-quick-links">
                <h3> Quick Links</h3>
                <ul>
                  <li onClick={handleClothingClick}>
                    <FaTshirt /> Clothing
                  </li>
                  <li onClick={handleFootwearClick}>
                    <FaShoePrints /> Footwear
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navbar;
